<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useConversationExport } from 'dashboard/composables/useConversationExport';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import Modal from 'dashboard/components/Modal.vue';
import ModalHeader from 'dashboard/components/ModalHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['close']);

const show = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');

const {
  isLoading,
  isFetchingExports,
  recentExports,
  fetchRecentExports,
  createExport,
} = useConversationExport();

const filters = ref({
  start_date: '',
  end_date: '',
  inbox_id: '',
  status: '',
  assignee_id: '',
  label: '',
});

const statusOptions = computed(() => [
  { value: '', label: t('CONVERSATION.EXPORT.FILTERS.STATUS.ALL') },
  { value: 'open', label: t('CONVERSATION.EXPORT.FILTERS.STATUS.OPEN') },
  {
    value: 'resolved',
    label: t('CONVERSATION.EXPORT.FILTERS.STATUS.RESOLVED'),
  },
  { value: 'pending', label: t('CONVERSATION.EXPORT.FILTERS.STATUS.PENDING') },
  { value: 'snoozed', label: t('CONVERSATION.EXPORT.FILTERS.STATUS.SNOOZED') },
]);

const activeFilters = computed(() => {
  const result = {};
  Object.entries(filters.value).forEach(([key, value]) => {
    if (value !== '') result[key] = value;
  });
  return result;
});

const handleExport = async () => {
  await createExport(activeFilters.value);
};

const handleClose = () => {
  show.value = false;
  emit('close');
};

const statusLabel = export_ => {
  const map = {
    pending: t('CONVERSATION.EXPORT.STATUS.PENDING'),
    processing: t('CONVERSATION.EXPORT.STATUS.PROCESSING'),
    ready: t('CONVERSATION.EXPORT.STATUS.READY'),
    failed: t('CONVERSATION.EXPORT.STATUS.FAILED'),
  };
  return map[export_.status] || export_.status;
};

const statusClass = export_ => {
  const map = {
    pending: 'text-n-slate-10',
    processing: 'text-n-brand',
    ready: 'text-n-teal-9',
    failed: 'text-n-ruby-9',
  };
  return map[export_.status] || '';
};

onMounted(fetchRecentExports);
</script>

<template>
  <Modal v-model:show="show" :on-close="handleClose">
    <ModalHeader
      :header-title="t('CONVERSATION.EXPORT.TITLE')"
      :header-content="t('CONVERSATION.EXPORT.DESCRIPTION')"
    />

    <div class="modal-content">
      <div class="grid grid-cols-2 gap-4">
        <Input
          v-model="filters.start_date"
          type="date"
          :label="t('CONVERSATION.EXPORT.FILTERS.START_DATE')"
        />
        <Input
          v-model="filters.end_date"
          type="date"
          :label="t('CONVERSATION.EXPORT.FILTERS.END_DATE')"
        />
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.EXPORT.FILTERS.INBOX') }}
        </label>
        <select
          v-model="filters.inbox_id"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option value="">
            {{ t('CONVERSATION.EXPORT.FILTERS.INBOX_ALL') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.EXPORT.FILTERS.STATUS.LABEL') }}
        </label>
        <select
          v-model="filters.status"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option
            v-for="opt in statusOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.EXPORT.FILTERS.AGENT') }}
        </label>
        <select
          v-model="filters.assignee_id"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option value="">
            {{ t('CONVERSATION.EXPORT.FILTERS.AGENT_ALL') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.EXPORT.FILTERS.LABEL') }}
        </label>
        <select
          v-model="filters.label"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option value="">
            {{ t('CONVERSATION.EXPORT.FILTERS.LABEL_ALL') }}
          </option>
          <option
            v-for="label_ in labels"
            :key="label_.id"
            :value="label_.title"
          >
            {{ label_.title }}
          </option>
        </select>
      </div>

      <div class="flex justify-end mt-6">
        <Button
          :label="t('CONVERSATION.EXPORT.BUTTON')"
          :is-loading="isLoading"
          @click="handleExport"
        />
      </div>

      <div class="mt-8">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
          {{ t('CONVERSATION.EXPORT.RECENT_EXPORTS.TITLE') }}
        </h3>

        <div v-if="isFetchingExports" class="flex justify-center py-4">
          <span class="text-sm text-n-slate-10">{{
            t('CONVERSATION.EXPORT.RECENT_EXPORTS.LOADING')
          }}</span>
        </div>

        <div
          v-else-if="recentExports.length === 0"
          class="text-sm text-n-slate-10 py-2"
        >
          {{ t('CONVERSATION.EXPORT.RECENT_EXPORTS.EMPTY') }}
        </div>

        <ul v-else class="divide-y divide-n-weak">
          <li
            v-for="export_ in recentExports"
            :key="export_.id"
            class="flex items-center justify-between py-3 gap-4"
          >
            <div class="flex flex-col min-w-0">
              <span class="text-xs text-n-slate-11">
                {{
                  messageTimestamp(
                    new Date(export_.created_at).getTime() / 1000
                  )
                }}
              </span>
              <span
                class="text-xs font-medium mt-0.5"
                :class="[statusClass(export_)]"
              >
                {{ statusLabel(export_) }}
              </span>
            </div>
            <a
              v-if="export_.file_url"
              :href="export_.file_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs font-medium text-n-brand hover:underline shrink-0"
            >
              {{ t('CONVERSATION.EXPORT.RECENT_EXPORTS.DOWNLOAD') }}
            </a>
          </li>
        </ul>
      </div>
    </div>
  </Modal>
</template>
